import { dealDuel, judgeDuel } from './carDuel';
import { roundSeconds } from './duelClock';
import { roundPoints } from './duelScoring';

/**
 * A run of duels, as a pure reducer (SPEC-007).
 *
 * The rule is one sentence: the run lasts exactly as long as the reader keeps
 * getting it right. What makes it worth a module rather than three `useState`
 * calls is the shape of the ENDING — a wrong answer does not end the run
 * silently, it ends it on the reveal, because the reader has to be told what the
 * answer was. That is the difference between a game that teaches somebody the
 * Yellowbird's 0–100 and a game that just says "wrong".
 *
 * The run ends in one of TWO ways and they are deliberately the same ending: a
 * wrong answer, and a round the clock ran out on. Both stop on the reveal, both
 * explain the answer, and both offer a new run — because a reader who did not
 * answer in time did not know it fast enough, which is exactly what the clock
 * was asking.
 *
 * Four statuses:
 *
 *   asking       a duel is on screen and unanswered, and the clock is running.
 *   revealed     it was answered correctly. The figures are open and there is
 *                another round waiting.
 *   over         it was answered wrongly. The figures are open, the run has
 *                ended, and the only way on is a new run.
 *   unavailable  this catalogue cannot be duelled. The content is FETCHED, so a
 *                pack that arrives short is a designed state and not an
 *                impossible one.
 *
 * `best` is carried THROUGH the run rather than read from storage inside it:
 * this module does not know Web Storage exists, and `application/game` is what
 * hands it in and writes it back when it moves. The SCORE is the same shape —
 * accumulated here, banked outside.
 *
 * `record` is the third of them and the newest (SPEC-010): the best SCORE this
 * device has ever run, as it stood when this run opened. It is the BAR the
 * collection's ladder is quoted against, and it is carried rather than derived,
 * because a target measured against a record the current run has already moved
 * is a target that runs away from the reader as they chase it.
 *
 * `claimRun` is the one thing that moves it, and it is not an exception to that
 * rule so much as the other half of it (SPEC-010 PR-10.11): a reader who takes
 * their car mid-run has SPENT the score that bought it, so the rung above is
 * quoted from there. Nothing else in this module reads `record` at all.
 *
 * TWO CLOCKS, and they are not a duplicate of each other (SPEC-007 PR-7.11).
 * `seconds` is the one a reader watches: a whole number, ticked down once a
 * second by `application/game`, and the only thing the counter and the drain bar
 * are ever drawn from. `askedAt` is a TIMESTAMP taken when the question was
 * dealt, and it exists because a round is now paid — and the reaction reported —
 * on the real interval between the question and the tap, which a counter that
 * moves in whole seconds cannot tell you: answering in 300ms and answering in
 * 1.2s both leave "14" on the clock, and paying them the same is the game
 * failing to notice the difference it just asked the reader to make.
 *
 * `now` is taken the way `random` is — a defaulted parameter resolved at CALL
 * time — so this module still never reads a clock of its own, and a suite that
 * pins `Date.now` pins every payment.
 */

export const RUN_STATUSES = ['asking', 'revealed', 'over', 'unavailable'];

/**
 * Open a run on the first duel this pool deals.
 *
 * `best` comes in from outside so that a device's record survives a run without
 * this module ever learning where it is kept.
 */
export function startRun(pool, {
    random = Math.random,
    best = 0,
    record = 0,
    now = Date.now,
} = {}) {
    // Round one, which is what makes a first question one of the easy ones.
    const duel = dealDuel(pool, { random, round: 1 });

    if (!duel) {
        return {
            status: 'unavailable',
            duel: null,
            verdict: null,
            streak: 0,
            best,
            record,
            score: 0,
            points: 0,
            round: 0,
            seconds: 0,
            askedAt: null,
            reaction: null,
            seen: [],
        };
    }

    return {
        status: 'asking',
        duel,
        verdict: null,
        streak: 0,
        best,
        // The bar the run is being measured against. Everything else on this
        // object moves with the round; this one moves only when a car is
        // claimed against it, and `claimRun` is the only thing that does.
        record,
        score: 0,
        // What the round just answered paid. Zero until one has been.
        points: 0,
        round: 1,
        // The allowance this round gets. Carried ON the run rather than derived
        // at the call site, so the thing that counts down and the thing that
        // draws the drain bar are reading one number.
        seconds: roundSeconds(1),
        // When the question was dealt, and what the round is paid against. The
        // reader's own clock starts here too — the entrance the cards play is
        // inside their allowance, because a question they cannot read yet is
        // still a question they are being timed on.
        askedAt: now(),
        // How long the last answer took, in milliseconds. Nothing until one has
        // been given.
        reaction: null,
        seen: [duel.key],
    };
}

/**
 * The clock ran out.
 *
 * Graded exactly as a wrong answer is, with one difference the reveal reads: no
 * card was chosen. `judgeDuel` already answers `chosen: null` and
 * `isCorrect: false` for an id that is not in the duel, so there is no second
 * grading path here — running out of time IS answering with nothing.
 */
export function expireRun(run) {
    if (!run || run.status !== 'asking') {
        return run;
    }

    return {
        ...run,
        status: 'over',
        verdict: judgeDuel(run.duel, null),
        // A round nobody answered pays nothing, and the reveal reads this to
        // know not to draw a "+points" that was never earned.
        points: 0,
        seconds: 0,
        // Nor a reaction. A round that ran out is not a slow answer, it is the
        // absence of one, and a hero reporting "15.00s" would be reading the
        // clock rather than the reader.
        reaction: null,
    };
}

/**
 * Grade the tap.
 *
 * A second answer to the same duel is ignored rather than re-graded. The reveal
 * leaves both cards in place and only `aria-disabled` (SPEC-007 D-18), so a
 * second press is reachable by construction — and a run whose streak could be
 * advanced twice on one question would be a run with no rule at all.
 */
export function answerRun(run, chosenCarId, { now = Date.now } = {}) {
    if (!run || run.status !== 'asking') {
        return run;
    }

    const verdict = judgeDuel(run.duel, chosenCarId);
    const streak = verdict.isCorrect ? run.streak + 1 : run.streak;
    // How long the reader actually took. Clamped at nothing, because a clock
    // that has gone backwards — a device correcting its time mid-round — must
    // not pay more than an instant answer.
    const reaction = Math.max(0, now() - (run.askedAt ?? now()));
    // Paid on the REAL interval rather than on the counter, so a fast answer is
    // worth more than a slow one at the resolution the reader can feel — and
    // matches the reaction time the hero is about to show them. The counter is
    // a whole number and would have paid a tenth of a second the same as a
    // second and a half (SPEC-007 D-73).
    const secondsLeft = roundSeconds(run.round) - reaction / 1000;
    // The DUEL goes with it, because what a round pays is partly what it asked:
    // a close pairing on a specialist figure in round fifteen is worth more than
    // "which of these two is faster" in round one, and the pool entry carries
    // both halves of that (SPEC-007 D-78).
    const points = verdict.isCorrect ? roundPoints(secondsLeft, run.round, run.duel) : 0;

    return {
        ...run,
        status: verdict.isCorrect ? 'revealed' : 'over',
        verdict,
        streak,
        points,
        reaction,
        score: run.score + points,
        // The record moves the moment it is beaten, not when the run ends: a
        // reader who reaches eight and then gets one wrong reached eight.
        best: Math.max(run.best, streak),
    };
}

/**
 * Take the car this run has just bought, and re-base the ladder on the score
 * that bought it (SPEC-010 PR-10.11).
 *
 * The run is otherwise untouched — same streak, same round, same verdict on
 * screen — which is the whole point of it: the reader banks a legend without
 * ending the run they banked it with, and the run they carry on is the same run.
 * The alternative shipped for a while, and it was that a car arrived only when a
 * reader got one wrong; a game where the reward for a good streak is the end of
 * it is a game that pays out on failure.
 *
 * MOVING THE BAR IS THE WHOLE OF IT. Leaving it where it was would quote the
 * next rung from a record this run has already beaten by a car's worth of
 * points, so the second car would cost a rung's margin MINUS the first — and the
 * fifth would be free. This module does not know what a car is, which is why it
 * takes no id and answers no prize: whether one is owed is
 * `carCollection.prizeForRun`, and where it is kept is `application/game`.
 *
 * A run with nothing on the board cannot claim, because a claim against a score
 * of nothing would re-base the bar on nothing and hand out the rest of the
 * ladder. `prizeForRun` refuses that run too, and the guard is here as well
 * because this function is reachable without it.
 */
export function claimRun(run) {
    if (!run || run.status === 'unavailable' || run.score <= 0) {
        return run;
    }

    return { ...run, record: run.score };
}

/**
 * Deal the next round.
 *
 * Only from `revealed` — an ended run is restarted, never advanced, and a run
 * that is still `asking` has a question on screen that would silently vanish.
 */
export function advanceRun(run, pool, { random = Math.random, now = Date.now } = {}) {
    if (!run || run.status !== 'revealed') {
        return run;
    }

    const round = run.round + 1;
    // The round is dealt AT its own number, which is the whole difficulty curve
    // reaching the pool: a later round may ask a harder subject and may pair two
    // cars the opening rounds would have considered too close to call.
    const duel = dealDuel(pool, { random, seen: run.seen, round });

    if (!duel) {
        return run;
    }

    return {
        ...run,
        status: 'asking',
        duel,
        verdict: null,
        points: 0,
        round,
        // A second less than the round before it. This is the whole difficulty
        // curve, and it is one line because `duelClock` owns the shape of it.
        seconds: roundSeconds(round),
        // The new question's own stopwatch, started here. Reset rather than
        // carried: a reaction left over from the last round would price this one
        // at the speed of the one before it.
        askedAt: now(),
        reaction: null,
        seen: [...run.seen, duel.key],
    };
}

/**
 * A new run that keeps the records the ended one set.
 *
 * `best` falls back to the ended run's own, because the streak record moves
 * inside a run and this module is where it moved. `record` does NOT: the score
 * record moves when the ended run is BANKED, which happens outside, so the
 * caller re-reads it and hands it in. A default of the old run's `record` here
 * would quote the next car against a bar the reader had just beaten.
 */
export function restartRun(run, pool, {
    random = Math.random,
    best,
    record,
    now = Date.now,
} = {}) {
    return startRun(pool, {
        random,
        now,
        best: best ?? run?.best ?? 0,
        record: record ?? run?.record ?? 0,
    });
}
